import { Alert, Badge, Box, Button, Code, Group, Text } from '@mantine/core';
import { IconAlertCircle, IconDownload, IconLock, IconLockOpen } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MessageResponse } from '@/api';
import {
  DecryptionError,
  decryptAndCacheMessage,
  getCachedDecryptedMessage,
  isNip44Supported,
  isNostrExtensionAvailable,
  NostrExtensionError,
} from '../../lib/nostr-decrypt';

interface NostrDecryptButtonProps {
  message: MessageResponse;
  onDecrypted?: (data: Record<string, unknown>) => void;
}

/**
 * Button component for decrypting Nostr-encrypted messages
 * Shows decrypted content inline after successful decryption
 */
export function NostrDecryptButton({ message, onDecrypted }: NostrDecryptButtonProps) {
  const { t } = useTranslation();
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decryptedData, setDecryptedData] = useState<Record<string, unknown> | null>(null);
  const [extensionAvailable, setExtensionAvailable] = useState(true);
  const [nip44Supported, setNip44Supported] = useState(true);

  // Check extension availability on mount
  useEffect(() => {
    setExtensionAvailable(isNostrExtensionAvailable());
    setNip44Supported(isNip44Supported());

    // Check cache for already decrypted message
    getCachedDecryptedMessage(message.id).then((cached) => {
      if (cached) {
        setDecryptedData(cached);
        onDecrypted?.(cached);
      }
    });
  }, [message.id, onDecrypted]);

  const handleDecrypt = async () => {
    if (!message.encryption_pubkey) {
      setError('Missing encryption pubkey - message cannot be decrypted');
      return;
    }

    setIsDecrypting(true);
    setError(null);

    try {
      // Use data_encrypted for nostr mode (the encrypted ciphertext)
      const encryptedData = message.data_encrypted || message.data;
      if (!encryptedData) {
        setError('No encrypted data available');
        return;
      }
      const data = await decryptAndCacheMessage(
        message.id,
        encryptedData,
        message.encryption_pubkey
      );
      setDecryptedData(data);
      onDecrypted?.(data);
    } catch (err) {
      if (err instanceof NostrExtensionError) {
        setError(err.message);
        setExtensionAvailable(false);
      } else if (err instanceof DecryptionError) {
        setError(err.message);
      } else {
        setError(t('messageTable.decryptionFailed'));
      }
    } finally {
      setIsDecrypting(false);
    }
  };

  const downloadDecryptedMessage = (format: 'json' | 'txt') => {
    if (!decryptedData) return;

    let data = '';
    if (format === 'json') {
      data = JSON.stringify(decryptedData, null, 2);
    } else {
      Object.keys(decryptedData).forEach((key) => {
        data += `${key}: ${decryptedData[key]}\n`;
      });
    }

    const element = document.createElement('a');
    const file = new Blob([data], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `formshive-message-${message.id}.${format}`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // If already decrypted, show the content
  if (decryptedData) {
    return (
      <Box>
        <Group gap="xs" mb="sm">
          <Badge color="green" leftSection={<IconLockOpen size={12} />}>
            Decrypted
          </Badge>
        </Group>
        <Code block>
          {Object.keys(decryptedData).map((key) => (
            <Box key={`decrypted-${key}`}>
              <Text>
                {key}:{' '}
                {Array.isArray(decryptedData[key])
                  ? (decryptedData[key] as unknown[]).join(', ')
                  : String(decryptedData[key])}
              </Text>
            </Box>
          ))}
        </Code>
        <Group mt="md">
          <Button
            onClick={() => downloadDecryptedMessage('json')}
            color="blue"
            size="compact-sm"
            leftSection={<IconDownload style={{ width: '70%', height: '70%' }} />}
          >
            {t('messageTable.downloadJson')}
          </Button>
          <Button
            onClick={() => downloadDecryptedMessage('txt')}
            color="blue"
            size="compact-sm"
            leftSection={<IconDownload style={{ width: '70%', height: '70%' }} />}
          >
            {t('messageTable.downloadTxt')}
          </Button>
        </Group>
      </Box>
    );
  }

  // Show error if extension not available
  if (!extensionAvailable || !nip44Supported) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title={t('messageTable.noNostrExtension')}
        color="yellow"
      >
        <Text size="sm">
          {!extensionAvailable
            ? 'Install a NIP-07 compatible Nostr extension (like nos2x, Alby, or Flamingo) to decrypt this message.'
            : 'Your Nostr extension does not support NIP-44. Please update or switch to a compatible extension.'}
        </Text>
      </Alert>
    );
  }

  return (
    <Box>
      <Button
        onClick={handleDecrypt}
        loading={isDecrypting}
        disabled={isDecrypting}
        leftSection={<IconLock size={16} />}
        color="green"
        variant="light"
      >
        {isDecrypting ? t('messageTable.decrypting') : t('messageTable.decryptWithNostr')}
      </Button>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={t('messageTable.decryptionFailed')}
          color="red"
          mt="sm"
        >
          {error}
        </Alert>
      )}
    </Box>
  );
}
